import * as yup from 'yup';
import { QuestionType, DifficultyLevel } from '@/services/certification.service';
import { isCleanText } from '@/utils/sanitize';

// VAPT #12/#13 - these forms use yupResolver, which skips the shared Input/Textarea clean-text rule
const CLEAN_TEXT_MESSAGE = 'Please use only letters, numbers and standard punctuation';
const cleanTextTest = (v?: string | null) => isCleanText(v);
// On the options array (not each option) so QuestionOptionsField shows the error
const optionsCleanTextTest = (options?: { text?: string }[]) => !options || options.every((opt) => isCleanText(opt.text));

/**
 * Validation schema for question options
 */
const optionSchema = yup.object().shape({
  id: yup.string().required('Option ID is required'),
  text: yup.string().required('Option text is required').min(1, 'Option text cannot be empty'),
  is_correct: yup.boolean().required('Option correctness flag is required'),
});

/**
 * Base question validation schema
 */
const baseQuestionSchema = yup.object().shape({
  question_text: yup
    .string()
    .required('Question text is required')
    .min(5, 'Question text must be at least 5 characters')
    .test('clean-text', CLEAN_TEXT_MESSAGE, cleanTextTest),
  question_type: yup
    .string()
    .oneOf(['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'TEXT_ANSWER'], 'Invalid question type')
    .required('Question type is required'),
  difficulty_level: yup
    .string()
    .oneOf(['EASY', 'MEDIUM', 'HARD'], 'Invalid difficulty level')
    .required('Difficulty level is required'),
  points: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string, null, or undefined
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      return value;
    })
    .required('Points are required')
    .min(1, 'Points must be at least 1')
    .max(100, 'Points cannot exceed 100')
    .integer('Points must be a whole number'),
  topic: yup.string().optional().test('clean-text', CLEAN_TEXT_MESSAGE, cleanTextTest),
  bloom_level: yup.string().optional().test('clean-text', CLEAN_TEXT_MESSAGE, cleanTextTest),
  explanation: yup.string().optional().test('clean-text', CLEAN_TEXT_MESSAGE, cleanTextTest),
  hint: yup.string().optional().test('clean-text', CLEAN_TEXT_MESSAGE, cleanTextTest),
  order: yup.number().optional(),
});

/**
 * Validation for SINGLE_CHOICE questions
 */
const singleChoiceValidation = yup.object().shape({
  options: yup
    .array()
    .of(optionSchema)
    .required('Options are required')
    .min(2, 'Single choice questions must have at least 2 options')
    .max(6, 'Single choice questions should not have more than 6 options')
    .test('has-one-correct', 'Single choice questions must have exactly one correct option', function (options) {
      if (!options) return false;
      const correctCount = options.filter((opt) => opt.is_correct).length;
      return correctCount === 1;
    })
    .test('all-options-filled', 'All options must have text', function (options) {
      if (!options) return true;
      return options.every((opt) => opt.text && opt.text.trim().length > 0);
    })
    .test('no-duplicate-options', 'Options cannot have duplicate text', function (options) {
      if (!options) return true;
      const texts = options.map((opt) => opt.text.trim().toLowerCase());
      const uniqueTexts = new Set(texts);
      return texts.length === uniqueTexts.size;
    })
    .test('options-clean-text', CLEAN_TEXT_MESSAGE, optionsCleanTextTest),
});

/**
 * Validation for MULTIPLE_CHOICE questions
 */
const multipleChoiceValidation = yup.object().shape({
  options: yup
    .array()
    .of(optionSchema)
    .required('Options are required')
    .min(3, 'Multiple choice questions must have at least 3 options')
    .test('has-min-correct', 'Multiple choice questions must have at least 2 correct options', function (options) {
      if (!options) return false;
      const correctCount = options.filter((opt) => opt.is_correct).length;
      return correctCount >= 2;
    })
    .test('not-all-correct', 'Multiple choice questions cannot have all options as correct', function (options) {
      if (!options) return true;
      const correctCount = options.filter((opt) => opt.is_correct).length;
      return correctCount < options.length;
    })
    .test('all-options-filled', 'All options must have text', function (options) {
      if (!options) return true;
      return options.every((opt) => opt.text && opt.text.trim().length > 0);
    })
    .test('no-duplicate-options', 'Options cannot have duplicate text', function (options) {
      if (!options) return true;
      const texts = options.map((opt) => opt.text.trim().toLowerCase());
      const uniqueTexts = new Set(texts);
      return texts.length === uniqueTexts.size;
    })
    .test('options-clean-text', CLEAN_TEXT_MESSAGE, optionsCleanTextTest),
});

/**
 * Validation for TRUE_FALSE questions
 */
const trueFalseValidation = yup.object().shape({
  options: yup
    .array()
    .of(optionSchema)
    .required('Options are required')
    .length(2, 'True/False questions must have exactly 2 options')
    .test('has-one-correct', 'True/False questions must have exactly one correct option', function (options) {
      if (!options) return false;
      const correctCount = options.filter((opt) => opt.is_correct).length;
      return correctCount === 1;
    })
    .test('all-options-filled', 'Both options must have text', function (options) {
      if (!options) return true;
      return options.every((opt) => opt.text && opt.text.trim().length > 0);
    })
    .test('no-duplicate-options', 'Options cannot have duplicate text', function (options) {
      if (!options) return true;
      const texts = options.map((opt) => opt.text.trim().toLowerCase());
      const uniqueTexts = new Set(texts);
      return texts.length === uniqueTexts.size;
    })
    .test('options-clean-text', CLEAN_TEXT_MESSAGE, optionsCleanTextTest),
});

/**
 * Validation for TEXT_ANSWER questions
 */
const textAnswerValidation = yup.object().shape({
  answer_keywords: yup
    .array()
    .of(yup.string().required())
    .required('Answer keywords are required')
    .min(1, 'At least one answer keyword is required')
    .test('keywords-not-empty', 'Keywords cannot be empty strings', function (keywords) {
      if (!keywords) return true;
      return keywords.every((keyword) => keyword.trim().length > 0);
    })
    // Checked on the array so the error shows under the keywords field
    .test('keywords-clean-text', CLEAN_TEXT_MESSAGE, (keywords) => !keywords || keywords.every(cleanTextTest)),
});

/**
 * Main validation schema for questions
 * Validates based on question_type
 */
export const questionValidationSchema = yup.lazy((values: any) => {
  const questionType = values.question_type as QuestionType;

  switch (questionType) {
    case 'SINGLE_CHOICE':
      return baseQuestionSchema.concat(singleChoiceValidation);

    case 'MULTIPLE_CHOICE':
      return baseQuestionSchema.concat(multipleChoiceValidation);

    case 'TRUE_FALSE':
      return baseQuestionSchema.concat(trueFalseValidation);

    case 'TEXT_ANSWER':
      return baseQuestionSchema.concat(textAnswerValidation);

    default:
      return baseQuestionSchema;
  }
});

/**
 * Type-safe validation function
 */
export const validateQuestion = async (data: any) => {
  try {
    await questionValidationSchema.validate(data, { abortEarly: false });
    return { valid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: Record<string, string> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { valid: false, errors };
    }
    return { valid: false, errors: { _form: 'Validation failed' } };
  }
};
